import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  sendPasswordResetEmail,
  createUserWithEmailAndPassword 
} from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { auth, db } from '../lib/firebase';

// Helper to get Firebase config for the secondary instance
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

class FirebaseAuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
  }

  initialize() {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userData = await this.getUserData(firebaseUser.uid);
        this.currentUser = userData ? { uid: firebaseUser.uid, ...userData } : null;
      } else {
        this.currentUser = null;
      }
      this.notifyAuthStateListeners(this.currentUser);
    });
  }

  async getUserData(uid) {
    try {
      const d = await getDoc(doc(db, 'users', uid));
      return d.exists() ? d.data() : null;
    } catch (e) {
      return null;
    }
  }

  async login(email, password) {
    try {
      const res = await signInWithEmailAndPassword(auth, email.trim(), password);
      const data = await this.getUserData(res.user.uid);
      return { success: true, user: data };
    } catch (e) {
      let message = 'Login failed.';
      if (e.code === 'auth/user-not-found') message = 'No account found with this email.';
      if (e.code === 'auth/wrong-password') message = 'Incorrect password.';
      return { success: false, message: e.message || message };
    }
  }

  /**
   * CREATE USER (Serverless Version)
   * This logic allows an admin to create a new user account (Tutor/Parent)
   * without being logged out themselves.
   */
  async createUser(userData) {
    let secondaryApp;
    try {
      const { email, password, firstName, lastName, role, ...rest } = userData;
      
      if (!email || !password) {
        throw new Error("Email and Password are required for new accounts.");
      }

      // 1. Initialize a temporary secondary app instance
      const secondaryAppName = `AdminAction_${Date.now()}`;
      secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);

      // 2. Create user in Firebase Auth via secondary instance
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth, 
        email, 
        password
      );
      const uid = userCredential.user.uid;

      // 3. Create the profile in Firestore using the MAIN database instance
      const profileData = {
        uid,
        id: uid,
        email,
        firstName,
        lastName,
        name: `${firstName} ${lastName}`,
        role: role || 'parent',
        status: 'active',
        createdAt: new Date().toISOString(),
        permissions: rest.permissions || {
          canManageStudents: role === 'admin',
          canManageUsers: role === 'admin',
          canViewReports: role === 'admin',
          canManageSessions: role !== 'parent',
          canManageParents: role === 'admin',
          canManageSubjects: role === 'admin'
        },
        ...rest
      };

      await setDoc(doc(db, 'users', uid), profileData);

      // 4. Cleanup: Sign out and delete secondary app to prevent memory leaks
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);
      
      return { success: true, uid };
    } catch (error) {
      console.error("❌ User Creation Error:", error);
      // Clean up if app was created but creation failed
      if (secondaryApp) await deleteApp(secondaryApp).catch(() => {});
      
      let message = error.message;
      if (error.code === 'auth/email-already-in-use') message = 'This email is already registered.';
      if (error.code === 'auth/weak-password') message = 'Password should be at least 6 characters.';
      
      return { success: false, message };
    }
  }

  async updateUser(uid, data) {
    try {
      await setDoc(doc(db, 'users', uid), { 
        ...data, 
        updatedAt: new Date().toISOString() 
      }, { merge: true });
      return { success: true };
    } catch (e) {
      return { success: false, message: e.message };
    }
  }

  async deleteUser(uid) {
    try {
      // NOTE: Client SDK cannot delete Auth accounts of other users.
      // We delete the Firestore record. The Auth account remains orphaned
      // unless deleted via Firebase Console or Admin SDK.
      await deleteDoc(doc(db, 'users', uid));
      return { success: true };
    } catch (e) {
      return { success: false, message: e.message };
    }
  }

  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (e) {
      return { success: false, message: e.message };
    }
  }

  async logout() {
    await signOut(auth);
    this.currentUser = null;
    return { success: true };
  }

  onAuthStateChanged(callback) {
    this.authStateListeners.push(callback);
    return () => this.authStateListeners = this.authStateListeners.filter(l => l !== callback);
  }

  notifyAuthStateListeners(user) {
    this.authStateListeners.forEach(c => c(user));
  }

  hasPermission(p) {
    if (this.currentUser?.role === 'admin') return true;
    return this.currentUser?.permissions?.[p] || false;
  }
}

const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;